import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Check, ExternalLink, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { messagesApi } from '../lib/api';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';

const OFFICE_EMAIL = 'easyspanishinsurance@enovaestates.com';
const OFFICE_PHONE = '+34 951 273 680';
const OFFICE_TEL_HREF = 'tel:+34951273680';
const OFFICE_ADDRESS_QUERY =
  'Urb.+Manilva+Beach+Fase+2,+Local+7,+29692+Manilva,+Malaga,+Spain';

export default function Contact() {
  const { t } = useI18n();
  const T = (k) => t(k, UI[k]);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await messagesApi.create({
        subject: form.subject,
        message: form.message,
        source: 'contact',
        name: form.name,
        email: form.email,
      });
      setSent(true);
    } catch (err) {
      alert(`${T('contact.form_error')}: ` + (err?.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Seo pageKey="contact" />

      {/* Hero */}
      <section className="hero-bg">
        <div className="container-x pt-16 pb-14 text-center max-w-3xl mx-auto">
          <span className="eyebrow" data-testid="contact-eyebrow">{T('contact.eyebrow')}</span>
          <h1
            data-testid="contact-title"
            className="mt-4 text-4xl md:text-5xl font-bold text-[#0b1f3a] tracking-tight"
          >
            {T('contact.title')}
          </h1>
          <p className="mt-4 text-slate-600">{T('contact.subtitle')}</p>
        </div>
      </section>

      {/* Info + Form */}
      <section className="py-16">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Contact info */}
          <div className="space-y-6" data-testid="contact-info-block">
            <InfoBlock
              icon={Mail}
              title={T('contact.email_label')}
              content={
                <a
                  href={`mailto:${OFFICE_EMAIL}`}
                  className="text-[#0d3a8a] font-semibold hover:underline break-all"
                  data-testid="contact-email-link"
                >
                  {OFFICE_EMAIL}
                </a>
              }
            />
            <InfoBlock
              icon={Phone}
              title={T('contact.phone_label')}
              content={
                <a
                  href={OFFICE_TEL_HREF}
                  className="text-[#0d3a8a] font-semibold hover:underline"
                  data-testid="contact-phone-link"
                >
                  {OFFICE_PHONE}
                </a>
              }
            />
            <InfoBlock
              icon={MapPin}
              title={T('contact.office_label')}
              content={
                <div className="space-y-1 text-slate-700 text-sm">
                  <p className="font-semibold text-[#0b1f3a]">Enova Estates SL</p>
                  <p>Urb. Manilva Beach Fase 2, Local 7</p>
                  <p>29692 Manilva, Malaga, Spain</p>
                  <a
                    href={`https://www.google.com/maps?q=${OFFICE_ADDRESS_QUERY}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#0d3a8a] font-semibold hover:underline pt-1"
                    data-testid="contact-maps-link"
                  >
                    {T('contact.open_maps')} <ExternalLink size={12} />
                  </a>
                </div>
              }
            />
            <InfoBlock
              icon={Clock}
              title={T('contact.hours_label')}
              content={<p className="text-slate-700 text-sm">{T('contact.hours_value')}</p>}
            />
            <div className="bg-[#f7fafd] border border-slate-200 rounded-2xl p-5">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                {T('contact.vat_label')}
              </p>
              <p className="mt-1 font-bold text-[#0b1f3a] tracking-wider">ESB93253060</p>
            </div>
          </div>

          {/* Contact form */}
          <div
            className="bg-white border border-slate-200 rounded-2xl p-7 card-lift"
            data-testid="contact-form-card"
          >
            <h2 className="text-xl font-semibold text-[#0b1f3a] mb-5">
              {T('contact.form_title')}
            </h2>
            {sent ? (
              <div className="text-center py-10" data-testid="contact-form-sent">
                <span className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center mb-4">
                  <Check size={24} />
                </span>
                <h3 className="text-xl font-semibold text-[#0b1f3a]">
                  {T('contact.form_sent_title')}
                </h3>
                <p className="text-slate-600 mt-2">{T('contact.form_sent_body')}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div>
                  <Label className="field-label">{T('contact.form_name')}</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <Label className="field-label">{T('contact.form_email')}</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <Label className="field-label">{T('contact.form_subject')}</Label>
                  <Input
                    required
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    data-testid="contact-input-subject"
                  />
                </div>
                <div>
                  <Label className="field-label">{T('contact.form_message')}</Label>
                  <Textarea
                    rows={5}
                    required
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    data-testid="contact-input-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  <Send size={16} /> {busy ? T('contact.form_sending') : T('contact.form_send')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20">
        <div className="container-x">
          <h2 className="text-lg font-semibold text-[#0b1f3a] mb-4">
            {T('contact.map_title')}
          </h2>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              title="Enova Estates office map"
              src={`https://www.google.com/maps?q=${OFFICE_ADDRESS_QUERY}&output=embed`}
              width="100%"
              height="380"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, content }) {
  return (
    <div className="flex items-start gap-4">
      <span className="icon-tile shrink-0"><Icon size={20} /></span>
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
          {title}
        </p>
        <div>{content}</div>
      </div>
    </div>
  );
}
